import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { trpc, trpcClient } from "@/hooks/trpc";
import { incrementVersion, protocolDisplayName } from "@brewbase/lib";
import type { FermentationProtocolId } from "@brewbase/db/types";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertCircleIcon, ListIcon, FileText, Sliders } from "lucide-react";
import { ProtocolHeader } from "@/features/fermentation-protocol-editor";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useSidebarConfig } from "@/hooks/use-sidebar-config";
import type { SidebarNavItem } from "@/components/nav/sidebar-store";
import type { ProtocolWithDetails } from "@/features/fermentation-protocol-editor/types";

export const Route = createFileRoute("/fermentations/protocols/$protocol_id")({
  component: ProtocolEditorLayout,
  params: z.object({
    protocol_id: z.coerce.number(),
  }),
  loader: async ({ params }) => {
    const protocol = await trpcClient.fermentations.protocols.get.query(
      params.protocol_id as FermentationProtocolId,
    );
    return { protocolName: protocol?.name ?? "Protocol" };
  },
  head: ({ loaderData }) => ({
    meta: [{ title: `BrewBase - ${loaderData.protocolName}` }],
  }),
});

function ProtocolEditorLayout() {
  const protocolId = Route.useParams().protocol_id as FermentationProtocolId;
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  const [isNewVersionDialogOpen, setIsNewVersionDialogOpen] = useState(false);
  const [newVersion, setNewVersion] = useState("");
  const [changelog, setChangelog] = useState("");
  const [isLockDialogOpen, setIsLockDialogOpen] = useState(false);

  const {
    data: protocol,
    isLoading,
    error,
  } = trpc.fermentations.protocols.get.useQuery(protocolId);

  const createNewVersionMutation =
    trpc.fermentations.protocols.createNewVersion.useMutation({
      onSuccess: (data) => {
        toast.success(`New version "${data.version}" created!`);
        utils.fermentations.protocols.invalidate();
        setIsNewVersionDialogOpen(false);
        navigate({
          to: "/fermentations/protocols/$protocol_id/overview",
          params: { protocol_id: data.id },
        });
      },
      onError: (error) => {
        toast.error(`Failed to create new version: ${error.message}`);
      },
    });

  const lockMutation = trpc.fermentations.protocols.lock.useMutation({
    onSuccess: () => {
      toast.success("Protocol locked");
      utils.fermentations.protocols.invalidate();
      setIsLockDialogOpen(false);
    },
    onError: (error) => {
      toast.error(`Failed to lock protocol: ${error.message}`);
    },
  });

  const handleLock = () => {
    lockMutation.mutate(protocolId);
  };

  const handleCreateNewVersion = () => {
    if (!newVersion.trim()) {
      toast.error("Version is required");
      return;
    }
    createNewVersionMutation.mutate({
      sourceProtocolId: protocolId,
      newVersion: newVersion.trim(),
    });
  };

  const openNewVersionDialog = async () => {
    // Ask the server: archived versions still hold their slot but are hidden
    // from every list, so a local increment can suggest a taken version.
    const suggested = await utils.fermentations.protocols.nextVersion
      .fetch(protocolId)
      .catch(() => null);
    setNewVersion(suggested ?? incrementVersion(protocol?.version ?? "1.0"));
    setChangelog("");
    setIsNewVersionDialogOpen(true);
  };

  if (isLoading) {
    return <ProtocolEditorSkeleton />;
  }

  if (error || !protocol) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <div className="text-center">
          <AlertCircleIcon className="w-10 h-10 mx-auto text-destructive mb-2" />
          <p className="text-sm text-muted-foreground">
            {error?.message ?? "Failed to load protocol"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <ProtocolContent
        protocol={protocol}
        onCreateNewVersion={openNewVersionDialog}
        isCreatingNewVersion={createNewVersionMutation.isPending}
        onLock={() => setIsLockDialogOpen(true)}
        isLocking={lockMutation.isPending}
      />

      <Dialog
        open={isNewVersionDialogOpen}
        onOpenChange={setIsNewVersionDialogOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Version</DialogTitle>
            <DialogDescription>
              Create a new version of "{protocol.name}". The new version will
              copy all attributes, feeds, tasks, and sample patterns.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-version">New Version</Label>
              <Input
                id="new-version"
                value={newVersion}
                onChange={(e) => setNewVersion(e.target.value)}
                placeholder="e.g., 2.0"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="changelog">What changed? (optional)</Label>
              <Textarea
                id="changelog"
                value={changelog}
                onChange={(e) => setChangelog(e.target.value)}
                placeholder="Describe the changes in this version..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsNewVersionDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateNewVersion}
              disabled={createNewVersionMutation.isPending}
            >
              {createNewVersionMutation.isPending
                ? "Creating..."
                : "Create Version"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isLockDialogOpen} onOpenChange={setIsLockDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Lock Protocol</DialogTitle>
            <DialogDescription>
              Locking "{protocol.name}" will prevent any further changes. This
              action cannot be undone. Create a new version if you need to make
              changes later.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsLockDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleLock}
              disabled={lockMutation.isPending}
            >
              {lockMutation.isPending ? "Locking..." : "Lock Protocol"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ProtocolContent({
  protocol,
  onCreateNewVersion,
  isCreatingNewVersion,
  onLock,
  isLocking,
}: {
  protocol: ProtocolWithDetails;
  onCreateNewVersion: () => void;
  isCreatingNewVersion: boolean;
  onLock: () => void;
  isLocking: boolean;
}) {
  const navigation: SidebarNavItem[] = [
    {
      type: "link",
      id: "overview",
      label: "Overview",
      icon: FileText,
      href: `/fermentations/protocols/${protocol.id}/overview`,
    },
    {
      type: "link",
      id: "parameters",
      label: "Parameters",
      icon: Sliders,
      href: `/fermentations/protocols/${protocol.id}/parameters`,
      count: protocol.parameters.length || undefined,
    },
  ];

  useSidebarConfig({
    layer: 2,
    parent: {
      label: "Protocols",
      icon: ListIcon,
      href: "/fermentations/protocols",
    },
    header: {
      title: protocolDisplayName(protocol.name),
      subtitle: `v${protocol.version}`,
    },
    navigation,
  });

  return (
    <div className="flex flex-col h-full min-w-0 overflow-hidden">
      <ProtocolHeader
        protocol={protocol}
        onCreateNewVersion={onCreateNewVersion}
        isCreatingNewVersion={isCreatingNewVersion}
        onLock={onLock}
        isLocking={isLocking}
      />
      <main className="flex-1 min-h-0 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}

function ProtocolEditorSkeleton() {
  return (
    <div className="flex-1 flex flex-col">
      <div className="border-b px-4 py-2.5">
        <div className="flex items-center gap-3">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-5 w-16" />
        </div>
      </div>
      <div className="flex-1 p-4 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    </div>
  );
}
